import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { ApneaMetricsData, ApneaTimeSeriesData } from '../../../types/apneaTimeSeries';
import {
  formatLevel,
  formatMinPerHour,
  formatShare,
  getScreeningOutcome,
  ScreeningOutcome,
  toHourlyRows,
} from '../../../utilities/apneaDisplay';

interface ApneaScreeningTableProps {
  data: ApneaTimeSeriesData | null;
  summary: ApneaMetricsData | null;
  startTime: number; // Timestamp in milliseconds
  error?: Error | null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
  verdict: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  table: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& td, & th': {
      padding: '3px 12px',
      fontSize: '0.8rem',
      lineHeight: 1.5,
    },
  },
  elevatedRow: {
    fontWeight: 700,
    backgroundColor: theme.palette.grey[200],
    // Keep the shading on paper: browsers drop backgrounds when printing unless told not to.
    WebkitPrintColorAdjust: 'exact',
    printColorAdjust: 'exact',
    '& td': { fontWeight: 700 },
  },
  notAssessableRow: {
    color: theme.palette.text.secondary,
    '& td': { color: theme.palette.text.secondary, fontStyle: 'italic' },
  },
  totalRow: {
    '& td': { fontWeight: 600, borderTop: `2px solid ${theme.palette.grey[400]}` },
  },
  level: {
    fontFamily: 'monospace',
    letterSpacing: 1,
    whiteSpace: 'nowrap',
  },
  interpretation: {
    marginTop: theme.spacing(1),
  },
  caption: {
    marginTop: theme.spacing(1),
    display: 'block',
  },
}));

const interpretationKey: Record<ScreeningOutcome, string> = {
  flagged: 'apneaChart.flagged',
  belowThreshold: 'apneaChart.belowThreshold',
  notAssessable: 'apneaChart.notAssessable',
};

/**
 * Sleep-disordered breathing screening
 *
 * Printed-report layout (project_management#519, table rework): a bold
 * verdict line, an hourly table in local clock time, and the interpretation
 * plus caption. No chart — the report is printed, so every figure has to be
 * legible on paper without hover, and the "level" column is plain text so it
 * survives monochrome printing.
 *
 * Wording rules (load-bearing):
 *  - a negative screen is "below the threshold", never "no apnea detected";
 *  - the burden is never mapped onto AHI severity bands;
 *  - `assessable: false` is an explicit message, not an empty table.
 *
 * The parent gates on the presence of `apnea` so this card never renders as
 * a phantom loading state (the #507 lesson).
 */
export const ApneaScreeningTable: React.FC<ApneaScreeningTableProps> = ({
  data,
  summary,
  startTime,
  error = null,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  if (error) {
    return (
      <Paper style={{ padding: 16 }}>
        <Typography color="error">{t('apneaChart.errorMessage')}</Typography>
      </Paper>
    );
  }

  const outcome = getScreeningOutcome(summary);

  if (!summary || (outcome === null && !data)) {
    return null;
  }

  const threshold = formatMinPerHour(summary.flagThresholdMinPerH);
  const burden = formatMinPerHour(summary.burdenMinPerH);
  const hours = summary.analysedHours != null ? summary.analysedHours.toFixed(1) : null;

  let verdict: string | null = null;

  if (burden !== null && hours !== null && outcome === 'flagged') {
    verdict = t('apneaChart.verdictFlagged', { burden, hours });
  } else if (burden !== null && hours !== null && outcome === 'belowThreshold') {
    verdict = t('apneaChart.verdictBelow', { burden, hours });
  }

  const interpretation = outcome === null
    ? null
    : t(
      threshold === null && outcome !== 'notAssessable'
        ? `${interpretationKey[outcome]}NoThreshold`
        : interpretationKey[outcome],
      { threshold },
    );

  const rowClass = (row: { elevated: boolean; assessable: boolean }): string | undefined => {
    if (row.elevated) {
      return classes.elevatedRow;
    }

    return row.assessable ? undefined : classes.notAssessableRow;
  };

  const table = outcome === 'notAssessable'
    ? null
    : toHourlyRows(data, startTime, summary.flagThresholdMinPerH);
  const showTable = table !== null && table.rows.length > 0;

  return (
    <Container className={classes.root} component={Paper} data-testid="apnea-screening-card">
      <Typography className={classes.title} variant="h6">
        {t('apneaChart.title')}
      </Typography>

      {verdict && (
        <Typography variant="body1" className={classes.verdict} data-testid="apnea-verdict">
          {verdict}
        </Typography>
      )}

      {showTable && table && (
        <>
          <Table size="small" className={classes.table} data-testid="apnea-hourly-table">
            <TableHead>
              <TableRow>
                <TableCell>{t('apneaChart.colTime')}</TableCell>
                <TableCell align="right">{t('apneaChart.colAnalysed')}</TableCell>
                <TableCell align="right">{t('apneaChart.colApnea')}</TableCell>
                <TableCell align="right">{t('apneaChart.colShare')}</TableCell>
                <TableCell>{t('apneaChart.colLevel')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row) => (
                <TableRow
                  key={row.startSec}
                  className={rowClass(row)}
                  data-testid={row.elevated ? 'apnea-row-elevated' : 'apnea-row'}
                >
                  <TableCell>{row.timeRange}</TableCell>
                  <TableCell align="right">{row.analysedMinutes}</TableCell>
                  <TableCell align="right">{row.apneaMinutes === null ? '–' : row.apneaMinutes}</TableCell>
                  <TableCell align="right">{formatShare(row.share)}</TableCell>
                  <TableCell className={classes.level}>
                    {row.assessable ? formatLevel(row.levelBlocks) : t('apneaChart.rowNotAssessable')}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className={classes.totalRow} data-testid="apnea-row-total">
                <TableCell>{t('apneaChart.rowTotal')}</TableCell>
                <TableCell align="right">{table.totalAnalysedMinutes}</TableCell>
                <TableCell align="right">{table.totalApneaMinutes}</TableCell>
                <TableCell align="right">{formatShare(table.totalShare)}</TableCell>
                <TableCell>
                  {threshold !== null
                    && t('apneaChart.elevatedHours', { threshold, count: table.elevatedHours })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Typography variant="caption" color="textSecondary" className={classes.caption}>
            {t('apneaChart.legend')}
          </Typography>
        </>
      )}

      {interpretation && (
        <Typography
          variant="body2"
          className={classes.interpretation}
          data-testid={`apnea-interpretation-${outcome}`}
        >
          {interpretation}
        </Typography>
      )}

      <Typography variant="caption" color="textSecondary" className={classes.caption}>
        {t('apneaChart.caption')}
      </Typography>
    </Container>
  );
};
